import { useEffect, useState } from 'react';
import { Table } from 'antd';
import { Button, DropdownMenu, Title } from '../../components/shared';
import { useDispatch, useSelector } from 'react-redux';
import { loadTags } from '../../store/automation/automation.actions';
import { selectTags } from '../../store/automation/automation.selector';
import { ITag } from '../../entities/automation.entities';
import { sendSingleRequest } from '../../apis';
import { showFeedback } from '../../store/ui/ui.actions';
import { invertColor, periodToStr } from '../../lib/common.helpers';
import { selectLang } from '../../store/ui/ui.selector';
import { translate } from '../../lib/lang.helper';
import TagDataModal from '../../components/automation/TagDataModal';

interface TableProps {
  tags: ITag[];
  onEditClick: (d: ITag) => void;
  onDeleteClick: (d: ITag) => void;
}

const TagsTable = ({ tags, onEditClick, onDeleteClick }: TableProps) => {
  const lang = useSelector(selectLang);

  const columns = [
    {
      title: translate(lang, 'Name'),
      key: 'name',
      render: (x: ITag) => <div>{x.name}</div>,
    },
    {
      title: translate(lang, 'Color'),
      key: 'color',
      render: (x: ITag) =>
        x.color && (
          <div
            style={{
              backgroundColor: x.color,
              padding: 3,
              borderRadius: 5,
              textAlign: 'center',
              fontWeight: 'bold',
              color: invertColor(x.color),
            }}
          >
            {x.color}
          </div>
        ),
    },
    {
      title: translate(lang, 'Lines'),
      key: 'lines',
      render: (x: ITag) => <div>{`tagged ${x.tag_lines.length} lines`}</div>,
    },
    {
      title: translate(lang, '_req_urgent_attention'),
      key: 'is_urgent',
      render: (x: ITag) => <div>{x.is_urgent ? 'Yes' : 'No'}</div>,
    },
    {
      title: translate(lang, '_req_attention_delayed'),
      key: 'period',
      render: (x: ITag) => (
        <div>
          {translate(
            lang,
            x.period !== null
              ? x.period
                ? `after ${periodToStr(x.period)}`
                : 'immediately'
              : '-',
          )}
        </div>
      ),
    },
    {
      title: '',
      key: 'more',
      render: (x: ITag) => (
        <div className='wrap'>
          <DropdownMenu
            data={x}
            column='isUtil'
            onEdit={() => onEditClick(x)}
            onDeleteRow={() => onDeleteClick(x)}
          />
        </div>
      ),
    },
  ];

  return (
    <Table
      className='table table--units'
      rowKey='id'
      pagination={false}
      columns={columns}
      dataSource={tags}
    />
  );
};

const TagsPage = () => {
  const dispatch = useDispatch<any>();
  const tags = useSelector(selectTags);
  const lang = useSelector(selectLang);

  const [showAdd, setShowAdd] = useState(false);
  const [editTag, setEditTag] = useState<ITag>();

  const deleteClick = async (x: ITag) => {
    if (!window.confirm(translate(lang, '_delete_tag')))
      return;

    const res = await sendSingleRequest({}, 'DELETE', `api/tag/${x.id}`, true);
    if (res.status) {
      await dispatch(loadTags());
    } else {
      dispatch(
        showFeedback({
          isMessage: true,
          message: translate(lang, res.data?.message ?? 'Failed'),
          type: 'error',
        }),
      );
    }
  };

  useEffect(() => {
    dispatch(loadTags());
  }, [dispatch]);

  return (
    <div className='h-calc-80 container w-100'>
      <div
        className='pb-32 pt-32 mb-32'
        style={{ marginLeft: 'auto', marginRight: 'auto', maxWidth: 1000 }}
      >
        <div className='d-flex mb-16 align-center justify-between'>
          <Title
            className='mb-16'
            size={6}
            color='black-3'
            align='default'
            fontWeight={500}
          >
            {translate(lang, 'Line tags')}
          </Title>
        </div>
        <div className='d-flex white-card pt-12 pl-16 mb-8'>
          <div className='d-flex align-center'>
            <Button
              color='blue'
              size={3}
              width='small'
              type='fill'
              onClick={() => setShowAdd(true)}
            >
              {`${translate(lang, 'Add')} ${translate(lang, 'Line tag')}`}
            </Button>
          </div>
        </div>
        <TagsTable
          tags={tags ?? []}
          onEditClick={x => setEditTag(x)}
          onDeleteClick={deleteClick}
        />
      </div>
      {showAdd && <TagDataModal onClose={() => setShowAdd(false)} />}
      {editTag && (
        <TagDataModal
          tag={editTag}
          editId={editTag.id}
          onClose={() => setEditTag(undefined)}
        />
      )}
    </div>
  );
};

export default TagsPage;
